import React, { useEffect, useState } from 'react';
import { Loader2, Percent, ShieldAlert, CheckCircle2, XCircle, Ban, Pencil, X } from 'lucide-react';
import api, { errText } from '../../lib/api';
import { useToast } from '../../hooks/use-toast';
import ConsultationBoard from '../../components/ConsultationBoard';
import SessionTypesEditor from '../../components/SessionTypesEditor';

const OFFER_STATUS = {
  pending: 'bg-amber-100 text-amber-700', approved: 'bg-green-100 text-green-700',
  rejected: 'bg-red-100 text-red-600', disabled: 'bg-mahogany/10 text-mahogany', none: 'bg-mahogany/10 text-mahogany',
};

const money = (cur, n) => `${(cur || 'usd').toUpperCase()} ${Number(n || 0).toFixed(2)}`;
const summarizeOptions = (o) => {
  const sts = o.session_types || [];
  if (!sts.length) return 'No session options';
  return sts.map((s) => `${s.name}${s.duration_min ? ` (${s.duration_min}m)` : ''} · ${Number(s.price || 0) > 0 ? money(o.private_lesson_currency, s.price) : 'Free'}`).join(' · ');
};

const OfferEditModal = ({ offer, onClose, onSaved }) => {
  const { toast } = useToast();
  const [f, setF] = useState({
    offers_private_lessons: offer.offers_private_lessons ?? true,
    session_types: offer.session_types || [],
    private_lesson_currency: offer.private_lesson_currency || 'usd',
    private_lesson_note: offer.private_lesson_note || '',
    private_availability_note: offer.private_availability_note || '',
    agreement_signature: 'Admin',
  });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const save = async () => {
    setSaving(true);
    try {
      await api.patch(`/consultations/my-offer/${offer.id}`, {
        ...f,
        session_types: (f.session_types || []).filter((s) => (s.name || '').trim()).map((s) => ({
          ...(s.id ? { id: s.id } : {}), name: s.name.trim(),
          duration_min: s.duration_min === '' || s.duration_min == null ? null : parseInt(s.duration_min, 10),
          price: s.price === '' || s.price == null ? 0 : parseFloat(s.price),
          description: (s.description || '').trim() || null,
        })),
      });
      toast({ title: 'Saved & approved' }); onSaved();
    } catch (e) { toast({ title: 'Failed', description: errText(e) }); } finally { setSaving(false); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" data-testid="offer-edit-modal">
      <div className="bg-cream rounded-3xl p-6 max-w-lg w-full max-h-[85vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-3"><h3 className="font-serif-display text-xl text-burgundy">Edit private sessions — {offer.name}</h3><button onClick={onClose}><X className="w-5 h-5 text-mahogany/70" /></button></div>
        <label className="flex items-center gap-2 text-sm text-mahogany"><input type="checkbox" checked={f.offers_private_lessons} onChange={(e) => set('offers_private_lessons', e.target.checked)} data-testid="admin-offer-enable" /> Offer private lessons (approving on save)</label>
        <div className="mt-3">
          <SessionTypesEditor value={f.session_types} onChange={(v) => set('session_types', v)} currency={f.private_lesson_currency} />
        </div>
        <input value={f.private_lesson_note} onChange={(e) => set('private_lesson_note', e.target.value)} placeholder="Note" className="mt-3 w-full bg-white rounded-lg px-3 py-2 border border-rose/50 text-sm" />
        <input value={f.private_availability_note} onChange={(e) => set('private_availability_note', e.target.value)} placeholder="Availability" className="mt-2 w-full bg-white rounded-lg px-3 py-2 border border-rose/50 text-sm" />
        <button onClick={save} disabled={saving} className="pill-btn mt-4" data-testid="admin-offer-save">{saving && <Loader2 className="w-4 h-4 animate-spin" />} Save &amp; approve</button>
      </div>
    </div>
  );
};

const OffersManager = ({ offers, onChange }) => {
  const { toast } = useToast();
  const [edit, setEdit] = useState(null);
  const [teachers, setTeachers] = useState([]);
  const [pick, setPick] = useState('');
  useEffect(() => { api.get('/teachers').then((r) => setTeachers(r.data || [])).catch(() => {}); }, []);
  const offerIds = new Set(offers.map((o) => o.id));
  const addable = teachers.filter((t) => !offerIds.has(t.id));
  const openNew = () => { const t = teachers.find((x) => x.id === pick); if (t) setEdit(t); else toast({ title: 'Pick a facilitator first' }); };
  const act = async (id, path, note) => {
    try { await api.post(`/admin/consultations/offers/${id}/${path}`, note !== undefined ? { note } : {}); toast({ title: 'Updated' }); onChange(); }
    catch (e) { toast({ title: 'Failed', description: errText(e) }); }
  };
  return (
    <div className="rounded-2xl bg-white/70 border border-rose/40 p-4" data-testid="offers-manager">
      <h2 className="font-serif-display text-xl text-burgundy mb-3">Facilitator offers &amp; approvals</h2>
      <div className="flex flex-wrap items-end gap-2 mb-4 p-3 rounded-xl bg-rose-soft/20 border border-rose/30">
        <label className="text-xs uppercase tracking-widest text-mahogany/70 flex-1 min-w-[220px]">Enable booking for a facilitator
          <select value={pick} onChange={(e) => setPick(e.target.value)} className="mt-1 w-full bg-white rounded-lg px-3 py-2 border border-rose/50 text-sm" data-testid="offer-teacher-pick">
            <option value="">Select a facilitator / teacher…</option>
            {addable.map((t) => <option key={t.id} value={t.id}>{t.name}{t.role ? ` — ${t.role}` : ''}</option>)}
          </select>
        </label>
        <button onClick={openNew} className="pill-btn" data-testid="offer-add-btn"><Pencil className="w-4 h-4" /> Set up sessions</button>
      </div>
      {!offers.length ? <p className="text-sm text-mahogany/60">No facilitator offers yet. Use the selector above to enable & configure sessions for a facilitator.</p> : (
        <div className="space-y-2">
          {offers.map((o) => (
            <div key={o.id} className="flex items-center justify-between gap-3 flex-wrap p-3 rounded-xl bg-white border border-rose/30" data-testid={`offer-row-${o.id}`}>
              <div>
                <div className="font-medium text-burgundy">{o.name} <span className={`text-[11px] px-2 py-0.5 rounded-full ${OFFER_STATUS[o.private_lesson_status] || ''}`}>{o.private_lesson_status}</span>{!o.user_id && <span className="ml-1 text-[10px] text-mahogany/50">(no linked login)</span>}</div>
                <div className="text-xs text-mahogany/60">{summarizeOptions(o)}{o.private_lesson_review_note ? ` · note: ${o.private_lesson_review_note}` : ''}</div>
              </div>
              <div className="flex flex-wrap gap-1.5">
                <button onClick={() => setEdit(o)} className="pill-btn-outline text-xs py-1.5" data-testid={`offer-edit-${o.id}`}><Pencil className="w-3.5 h-3.5" /> Edit</button>
                {o.private_lesson_status !== 'approved' && <button onClick={() => act(o.id, 'approve')} className="pill-btn text-xs py-1.5" data-testid={`offer-approve-${o.id}`}><CheckCircle2 className="w-3.5 h-3.5" /> Approve</button>}
                <button onClick={() => { const n = window.prompt('Reason for rejection (shared as a comment):', ''); if (n !== null) act(o.id, 'reject', n); }} className="pill-btn-outline text-xs py-1.5" data-testid={`offer-reject-${o.id}`}><XCircle className="w-3.5 h-3.5" /> Reject</button>
                {o.private_lesson_status === 'approved' && <button onClick={() => { const n = window.prompt('Reason for disabling (optional):', ''); act(o.id, 'disable', n || ''); }} className="pill-btn-outline text-xs py-1.5" data-testid={`offer-disable-${o.id}`}><Ban className="w-3.5 h-3.5" /> Disable</button>}
              </div>
            </div>
          ))}
        </div>
      )}
      {edit && <OfferEditModal offer={edit} onClose={() => setEdit(null)} onSaved={() => { setEdit(null); onChange(); }} />}
    </div>
  );
};

const AdminConsultations = () => {
  const { toast } = useToast();
  const [items, setItems] = useState([]);
  const [offers, setOffers] = useState([]);
  const [settings, setSettings] = useState({ commission_percent: 20, facilitator_agreement: '', client_waiver: '' });
  const [loading, setLoading] = useState(true);
  const [savingC, setSavingC] = useState(false);
  const [showWaivers, setShowWaivers] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [list, s, offs] = await Promise.all([
        api.get('/admin/consultations').then((r) => r.data || []).catch(() => []),
        api.get('/admin/consultations/settings').then((r) => r.data).catch(() => null),
        api.get('/admin/consultations/offers').then((r) => r.data || []).catch(() => []),
      ]);
      setItems(list); setOffers(offs); if (s) setSettings(s);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const act = (fn, ok) => async (...a) => { try { await fn(...a); if (ok) toast({ title: ok }); load(); } catch (e) { toast({ title: 'Action failed', description: errText(e) }); } };
  const onAccept = act((id) => api.post(`/admin/consultations/${id}/accept`), 'Accepted');
  const onReject = act((id) => api.post(`/admin/consultations/${id}/reject`), 'Declined');
  const onPropose = act((id, when) => api.post(`/admin/consultations/${id}/propose`, { proposed_datetime: when }), 'Time proposed');
  const onPayout = act((id, proof) => api.post(`/admin/consultations/${id}/mark-payout-paid`, { proof_url: proof }), 'Payout marked paid');
  const onRefund = act((id) => api.post(`/admin/consultations/${id}/refund`), 'Refunded');

  const saveCommission = async () => {
    setSavingC(true);
    try { await api.put('/admin/consultations/commission', { percent: parseFloat(settings.commission_percent) || 0 }); toast({ title: 'Commission saved' }); }
    catch (e) { toast({ title: 'Failed', description: errText(e) }); } finally { setSavingC(false); }
  };
  const saveWaiver = async (key, text) => {
    try { await api.put(`/admin/consultations/${key}`, { text }); toast({ title: 'Saved' }); }
    catch (e) { toast({ title: 'Failed', description: errText(e) }); }
  };

  if (loading) return <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-mahogany" /></div>;

  return (
    <div className="space-y-6" data-testid="admin-consultations-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h1 className="font-serif-display text-3xl text-burgundy">Consultations</h1>
        <button onClick={() => setShowWaivers((v) => !v)} className="pill-btn-outline" data-testid="toggle-waivers"><ShieldAlert className="w-4 h-4" /> Agreements &amp; waivers</button>
      </div>

      <div className="rounded-2xl bg-white/70 border border-rose/40 p-4 flex items-end gap-3 flex-wrap">
        <label className="text-xs uppercase tracking-widest text-mahogany/70"><Percent className="w-3.5 h-3.5 inline" /> Platform commission %
          <input type="number" step="0.5" value={settings.commission_percent} onChange={(e) => setSettings((s) => ({ ...s, commission_percent: e.target.value }))} className="mt-1 block w-32 bg-white rounded-lg px-3 py-2 border border-rose/50 text-sm" data-testid="commission-input" />
        </label>
        <button onClick={saveCommission} disabled={savingC} className="pill-btn" data-testid="save-commission">{savingC && <Loader2 className="w-4 h-4 animate-spin" />} Save</button>
        <p className="text-xs text-mahogany/60">Facilitator payout = price − commission. Payouts are settled offline (check); mark them paid + attach proof below.</p>
      </div>

      {showWaivers && (
        <div className="grid md:grid-cols-2 gap-4" data-testid="waivers-panel">
          <div className="rounded-2xl bg-white/70 border border-rose/40 p-4">
            <div className="text-sm font-medium text-burgundy mb-2">Facilitator agreement</div>
            <textarea value={settings.facilitator_agreement} onChange={(e) => setSettings((s) => ({ ...s, facilitator_agreement: e.target.value }))} rows={8} className="w-full bg-white rounded-lg px-3 py-2 border border-rose/50 text-sm" data-testid="fac-agreement-text" />
            <button onClick={() => saveWaiver('facilitator-agreement', settings.facilitator_agreement)} className="pill-btn mt-2 text-sm">Save</button>
          </div>
          <div className="rounded-2xl bg-white/70 border border-rose/40 p-4">
            <div className="text-sm font-medium text-burgundy mb-2">Client booking waiver</div>
            <textarea value={settings.client_waiver} onChange={(e) => setSettings((s) => ({ ...s, client_waiver: e.target.value }))} rows={8} className="w-full bg-white rounded-lg px-3 py-2 border border-rose/50 text-sm" data-testid="client-waiver-text" />
            <button onClick={() => saveWaiver('client-waiver', settings.client_waiver)} className="pill-btn mt-2 text-sm">Save</button>
          </div>
        </div>
      )}

      <ConsultationBoard items={items} isAdmin onAccept={onAccept} onReject={onReject} onPropose={onPropose} onPayout={onPayout} onRefund={onRefund} />

      <OffersManager offers={offers} onChange={load} />
    </div>
  );
};

export default AdminConsultations;
